/**
 * Builds a display name from firstName and lastName.
 * Handles null/undefined values and trims extra whitespace.
 */
export function getContactName(
  contact: { firstName?: string | null; lastName?: string | null } | null | undefined
): string {
  if (!contact) return '';
  return `${contact.firstName || ''} ${contact.lastName || ''}`.trim();
}

/** Minimal contact shape — the permit and the account both satisfy it structurally. */
interface ContactLike {
  phoneNumbers?: { number?: string }[];
  emailAddresses?: string[];
}

/**
 * First phone / email present across the given contacts, in preference order.
 *
 * ## Invariants:
 * - Resolves per field, not per contact. Why: the api hands back an initialised-but-empty
 *   `contact` on a permit, so `permit.contact ?? account.contact` keeps the empty object and
 *   shows nothing even when the account holds the values (the reverse of PB-3756). A permit
 *   carrying only a phone must still fall back for its email.
 */
export function resolveContactDetails(...contacts: (ContactLike | null | undefined)[]): {
  phone: string;
  email: string;
} {
  const phone = contacts.map((c) => c?.phoneNumbers?.[0]?.number).find((v) => !!v) ?? '';
  const email = contacts.map((c) => c?.emailAddresses?.[0]).find((v) => !!v) ?? '';
  return { phone, email };
}

/**
 * Every phone / email from the FIRST contact that carries any, resolved per field.
 *
 * The list-returning sibling of {@link resolveContactDetails}, for the edit form — which manages
 * the whole list rather than just the first of each, so it cannot use the scalar version.
 *
 * ## Invariants:
 * - Resolves per field, not per contact, for the reason stated on {@link resolveContactDetails}:
 *   the api hands back an initialised-but-empty `contact` on a permit, so `permit.contact ??
 *   account.contact` keeps the empty object and yields nothing while the account holds the
 *   values. That is what left the Edit Contact form blank for a permit whose details the page
 *   behind it was displaying (PB-4864).
 * - Takes the first contact with a NON-EMPTY list, not the first contact with the key present.
 *   Why: an empty array is the same "nothing here" that the empty object is, and stopping on it
 *   reintroduces the bug one level down.
 * - Returns the winning list WHOLE rather than concatenating. Why: these are alternative records
 *   of the same person, not additive ones — merging would show a stale account address beside the
 *   permit-level one that replaced it, and hand both back on save.
 */
export function resolveContactLists(...contacts: (ContactLike | null | undefined)[]): {
  phones: { number?: string }[];
  emails: string[];
} {
  const phones =
    contacts
      .map((c) => (c?.phoneNumbers ?? []).filter((p) => !!p?.number))
      .find((list) => list.length > 0) ?? [];
  const emails =
    contacts.map((c) => (c?.emailAddresses ?? []).filter(Boolean)).find((list) => list.length > 0) ??
    [];
  return { phones, emails };
}

/**
 * Formats a stored phone number for display as US-style `(999) 531-9146`.
 *
 * ## Invariants:
 * - Anything that isn't a US 10-digit number is returned untouched. Why: the api stores
 *   contact numbers in E.164, so a non-US number would otherwise be reshaped into a
 *   US-looking one that can't be dialled.
 */
export function formatUsPhone(phone: string | null | undefined): string {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  const local = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (local.length !== 10) return phone;
  return `(${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`;
}

/**
 * The account's login email, used as a fallback when `contactInfo` carries no email.
 *
 * The identity SDK stores the login under the `username` localStorage key; for
 * email-based logins that value is the user's email. SSR-safe — returns '' off the
 * browser, and only returns the stored value when it looks like an email (so a
 * non-email username is never shown as one).
 */
export function getLoginEmail(): string {
  if (typeof window === 'undefined') return '';
  const username = window.localStorage.getItem('username') ?? '';
  return username.includes('@') ? username : '';
}

/**
 * Splits a full name string into firstName and lastName.
 * The first whitespace-delimited token becomes firstName;
 * everything after becomes lastName (or empty string if single token).
 */
export function parseFullName(fullName: string): { firstName: string; lastName: string } {
  const trimmed = fullName.trim();
  const parts = trimmed.split(/\s+/);
  const firstName = parts[0] || '';
  const lastName = parts.length > 1 ? parts.slice(1).join(' ') : '';
  return { firstName, lastName };
}
